import React, { useEffect, useState } from "react";
import {
  MdClose,
  MdPrint,
  MdCheckCircle,
  MdWarningAmber,
  MdBusiness,
  MdLocationOn,
  MdEmail,
  MdReceiptLong,
} from "react-icons/md";
import Modal from "../../../../components/modal";
import api from "../../../../services/axios";
import { toast } from "react-toastify";
import {
  BillingTypeName,
  EmitInvoiceResult,
  SiimpPreview,
  siimpResultadoMensagem,
} from "../../../../interfaces/Financial";

interface ClientFiscal {
  id: number;
  legalName: string;
  tradeName: string;
  document: string;
  email: string;
  phone: string;
  address: string | null;
  municipalRegistration: string | null;
  emailNF: string | null;
  emailBilling: string | null;
  paymentTermDays: number | null;
  billingDay: number | null;
  paymentMethod: number | null;
}

const PAYMENT_METHOD: Record<number, string> = {
  1: "Boleto",
  2: "Pix",
  3: "Transferência Bancária",
  4: "Cartão",
  5: "Outro",
};

const NATUREZA_FISCAL: Record<number, string> = {
  1: "Tributação no município",
  2: "Tributação fora do município",
  3: "Exportação de serviços",
  4: "Isenção",
  5: "Imune",
};

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const fmtCNPJ = (doc: string) => {
  const d = doc.replace(/\D/g, "");
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return doc;
};

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface Props {
  billingId: number;
  clientId: number;
  clientName: string;
  amount: number;
  billingType?: number | null;
  monthRef: number;
  yearRef: number;
  onClose: () => void;
  onSuccess: () => void;
}

const InvoiceDocModal: React.FC<Props> = ({
  billingId,
  clientId,
  clientName,
  amount,
  billingType,
  monthRef,
  yearRef,
  onClose,
  onSuccess,
}) => {
  const [client, setClient] = useState<ClientFiscal | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [siimp, setSiimp] = useState<SiimpPreview | null>(null);
  const [discriminacao, setDiscriminacao] = useState("");
  const [vencimento, setVencimento] = useState("");

  useEffect(() => {
    // Sem a prévia (erro ou integração desligada) o modal segue o fluxo antigo, sem campos do SIIMP.
    api
      .get<SiimpPreview>(`/Financial/Siimp/Preview/${billingId}`)
      .then((res) => {
        setSiimp(res.data);
        setDiscriminacao(res.data.discriminacao);
        setVencimento(res.data.vencimento);
      })
      .catch(() => setSiimp(null));
  }, [billingId]);

  useEffect(() => {
    (async () => {
      try {
        // Tenta endpoint individual primeiro; fallback para lista
        try {
          const res = await api.get<ClientFiscal>(`/Client/Clients/${clientId}`);
          setClient(res.data);
        } catch {
          const res = await api.get<ClientFiscal[]>("/Client/Clients");
          const found = res.data.find((c) => c.id === clientId) ?? null;
          setClient(found);
        }
      } catch {
        toast.error("Não foi possível carregar os dados fiscais do cliente.", {
          position: "top-right",
          autoClose: 4000,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      if (siimp?.habilitado) {
        const res = await api.post<EmitInvoiceResult>(`/Financial/EmitInvoice/${billingId}`, {
          discriminacao,
          vencimento,
        });
        const { ok, texto } = siimpResultadoMensagem(res.data);
        if (ok) toast.success(texto, { position: "top-right", autoClose: 6000 });
        else toast.error(texto, { position: "top-right", autoClose: 10000 });
      } else {
        await api.post(`/Financial/EmitInvoice/${billingId}`);
        toast.success("Nota fiscal emitida com sucesso.", { position: "top-right", autoClose: 4000 });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const m = err?.response?.data;
      const conflito = err?.response?.status === 409;
      toast.error(typeof m === "string" && m ? m : conflito ? "Esta cobrança está sendo processada agora." : "Falha ao emitir NF.", {
        position: "top-right",
        autoClose: conflito ? 6000 : 4000,
      });
      if (conflito) {
        // Outra execução está avançando esta cobrança: recarrega a lista e fecha o modal.
        onSuccess();
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => window.print();

  const missing: string[] = [];
  if (client) {
    if (!client.document) missing.push("CNPJ");
    if (!client.address) missing.push("Endereço");
    if (!client.municipalRegistration) missing.push("Inscrição Municipal");
    if (!client.emailNF) missing.push("E-mail NF");
  }

  return (
    <Modal onClose={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-dv-gradient px-6 py-5 text-white">
          <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <MdReceiptLong className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                  Emissão de Nota Fiscal
                </p>
                <h3 className="text-lg font-extrabold leading-tight">{clientName}</h3>
                <p className="text-xs text-white/80">
                  OS #{billingId} · {MONTHS[monthRef - 1]} {yearRef} · {fmtMoney(amount)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                title="Imprimir / salvar PDF"
                className="rounded-xl p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <MdPrint className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                aria-label="Fechar"
                onClick={onClose}
                className="rounded-xl p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <MdClose className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-dv-blue-500 border-t-transparent" />
            </div>
          ) : !client ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              Dados fiscais não encontrados para este cliente.
            </p>
          ) : (
            <div className="space-y-5">
              {/* Aviso de campos faltando */}
              {missing.length > 0 && (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 dark:bg-amber-900/20">
                  <MdWarningAmber className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                      Dados incompletos no cadastro
                    </p>
                    <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                      Campos não preenchidos: <strong>{missing.join(", ")}</strong>. Complete no cadastro de clientes antes de emitir.
                    </p>
                  </div>
                </div>
              )}

              {/* Cobrança */}
              <section>
                <SectionTitle>Dados da Cobrança</SectionTitle>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Field label="OS Nº" value={`#${billingId}`} />
                  <Field label="Período" value={`${MONTHS[monthRef - 1]} / ${yearRef}`} />
                  {billingType != null && (
                    <Field label="Tipo" value={BillingTypeName[billingType as 1 | 2 | 3] ?? "—"} />
                  )}
                  <Field label="Valor" value={fmtMoney(amount)} highlight />
                  {client.paymentTermDays != null && (
                    <Field label="Prazo de pagamento" value={`${client.paymentTermDays} dias`} />
                  )}
                  {client.billingDay != null && (
                    <Field label="Dia de cobrança" value={`Dia ${client.billingDay}`} />
                  )}
                  {client.paymentMethod != null && (
                    <Field
                      label="Forma de pagamento"
                      value={PAYMENT_METHOD[client.paymentMethod] ?? "—"}
                    />
                  )}
                </div>
              </section>

              {siimp?.habilitado && (
                <section>
                  <SectionTitle icon={<MdReceiptLong className="h-4 w-4" />}>
                    NFS-e e fatura no SIIMP
                  </SectionTitle>
                  <label htmlFor="nfse-discriminacao" className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    Discriminação do serviço (sai na nota)
                  </label>
                  <textarea
                    id="nfse-discriminacao"
                    value={discriminacao}
                    onChange={(e) => setDiscriminacao(e.target.value)}
                    rows={7}
                    maxLength={4000}
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 font-mono text-xs text-navy-700 focus:border-dv-blue-500 focus:outline-none dark:border-white/15 dark:bg-navy-900 dark:text-white"
                  />
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label htmlFor="fatura-vencimento" className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        Vencimento da fatura
                      </label>
                      <input
                        id="fatura-vencimento"
                        type="date"
                        value={vencimento}
                        onChange={(e) => setVencimento(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 focus:border-dv-blue-500 focus:outline-none dark:border-white/15 dark:bg-navy-900 dark:text-white"
                      />
                    </div>
                    <Field label="Serviço NFS-e (SIIMP)" value={String(siimp.nfseServicoId)} />
                    <Field label="Natureza fiscal" value={NATUREZA_FISCAL[siimp.naturezaFiscal] ?? String(siimp.naturezaFiscal)} />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Ao confirmar, o NINA emite a NFS-e no SIIMP, cria a fatura ligada a ela e marca a cobrança
                    como recebida quando o financeiro der baixa na fatura lá. A nota é um documento fiscal:
                    revise o texto e o valor antes.
                  </p>
                </section>
              )}

              {/* Tomador de serviços */}
              <section>
                <SectionTitle icon={<MdBusiness className="h-4 w-4" />}>
                  Tomador de Serviços
                </SectionTitle>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Razão Social" value={client.legalName} />
                  <Field label="Nome Fantasia" value={client.tradeName} />
                  <Field
                    label="CNPJ"
                    value={client.document ? fmtCNPJ(client.document) : "—"}
                    missing={!client.document}
                  />
                  <Field
                    label="Inscrição Municipal"
                    value={client.municipalRegistration ?? "—"}
                    missing={!client.municipalRegistration}
                  />
                </div>
              </section>

              {/* Endereço */}
              <section>
                <SectionTitle icon={<MdLocationOn className="h-4 w-4" />}>
                  Endereço
                </SectionTitle>
                <Field
                  label="Endereço completo"
                  value={client.address ?? "—"}
                  missing={!client.address}
                  full
                />
              </section>

              {/* Contato */}
              <section>
                <SectionTitle icon={<MdEmail className="h-4 w-4" />}>
                  Contato
                </SectionTitle>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field
                    label="E-mail para NF"
                    value={client.emailNF ?? "—"}
                    missing={!client.emailNF}
                  />
                  <Field
                    label="E-mail para cobrança"
                    value={client.emailBilling ?? client.email}
                  />
                  {client.phone && (
                    <Field label="Telefone" value={client.phone} />
                  )}
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || loading || !client || (siimp?.habilitado === true && (!discriminacao.trim() || !vencimento))}
            className="dv-sheen flex items-center gap-2 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MdCheckCircle className="h-4 w-4" aria-hidden="true" />
            {submitting ? "Emitindo…" : siimp?.habilitado ? "Emitir NFS-e no SIIMP" : "Confirmar emissão de NF"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode; icon?: React.ReactNode }> = ({
  children,
  icon,
}) => (
  <div className="mb-2.5 flex items-center gap-1.5">
    {icon && <span className="text-dv-blue-500 dark:text-dv-cyan-400">{icon}</span>}
    <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {children}
    </h4>
    <div className="flex-1 border-t border-gray-100 dark:border-white/10" />
  </div>
);

const Field: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
  missing?: boolean;
  full?: boolean;
}> = ({ label, value, highlight, missing, full }) => (
  <div className={full ? "col-span-full" : ""}>
    <p className="mb-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">{label}</p>
    <p
      className={`text-sm font-semibold ${
        missing
          ? "italic text-amber-600 dark:text-amber-400"
          : highlight
          ? "text-dv-blue-600 dark:text-dv-cyan-300"
          : "text-navy-700 dark:text-white"
      }`}
    >
      {value}
    </p>
  </div>
);

export default InvoiceDocModal;
